import { createAsyncThunk } from "@reduxjs/toolkit";
import api, { apiWithFormDataMethods } from "../../../utils/constants/api";
import { ApiResponse } from "../../../types/APIResponse";
import { Document, EditReportLocationInput, Report, User } from "../../../types";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { GetPrematchedFoundItemsProps, LinkFoundItemWithLostItemProps, PrematchedFoundItems } from "../../../types/PreMatchedFoundItems";
import { STATUS_CODE } from "../../../utils/constants/auth";
import { ShippingOption, UpdateShippingOptionResponse, Package, UpdateShippingOptionAddressRequestPayload } from "../../../types/ShipTypes";
import { AdminUser } from "../user/types";

export const getReportAsyncThunk = createAsyncThunk<Report, { id: string; withDeleted?: boolean }, { rejectValue: string }>("lostItem/getReportAsyncThunk", async ({ id, withDeleted }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await api.get(`${apiEndpoints.reportDetail.getLostItemInfoByTrackingId}?trackingId=${id}${withDeleted ? `&withDeleted=${withDeleted}` : ""}`);
    if (response.success) {
      return { ...response.data, isFoundItem: false };
    } else {
      throw new Error(response.message || "Failed to fetch lost item information");
    }
  } catch (error: any) {
    console.error("error: ", error);
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const updateUserDetail = createAsyncThunk<User, { userId: string; userDetails: User }, { rejectValue: string }>("lostItem/updateUserDetail", async ({ userId, userDetails }, { rejectWithValue }) => {
  try {
    const response = await api.put<ApiResponse<User>>(`${apiEndpoints.reportDetail.updateUserDetail}/${userId}`, { data: userDetails });
    return response.data; // Access the `data` field containing the updated user details
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to update user details");
  }
});

export const updateItemDetail = createAsyncThunk<Report, { id: string | number; formData: FormData }, { rejectValue: string }>("lostItem/updateItemDetail", async ({ id, formData }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await apiWithFormDataMethods.put(`${apiEndpoints.reportDetail.updateItemDetail}/${id}`, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const updateLocationDetail = createAsyncThunk<Report, { id: string | number; data: EditReportLocationInput }, { rejectValue: string }>("lostItem/updateLocationDetail", async ({ id, data }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await api.put(`${apiEndpoints.reportDetail.updateLocationDetail}/${id}`, { data });
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const uploadDocument = createAsyncThunk<Report, { id: string | number; formData: FormData }, { rejectValue: string }>("lostItem/uploadDocument", async ({ id, formData }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await apiWithFormDataMethods.put(`${apiEndpoints.reportDetail.uploadDocument}/${id}`, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const deleteDocument = createAsyncThunk<string | number, { id: string | number }, { rejectValue: string }>("lostItem/deleteDocument", async ({ id }, thunkAPI) => {
  try {
    const response: ApiResponse<any> = await api.delete(`${apiEndpoints.reportDetail.deleteDocument}/${id}`);
    if (response.success) {
      return id;
    } else {
      throw new Error(response.message || "Failed to delete document");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const updateLostItemIsUrgent = createAsyncThunk("columns/updateLostItemIsUrgent", async ({ id, isUrgent }: { id: string; isUrgent: boolean }, thunkAPI) => {
  try {
    const response: ApiResponse<undefined> = await api.put(`${apiEndpoints.reportDetail.updateUrgentStatus}/${id}`, { data: { isUrgent } });

    return { id, isUrgent };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const passPendingRequest = createAsyncThunk("columns/passPendingRequest", async ({ id, unlinkReason, isDeleteRequest }: { id: string; unlinkReason: string; isDeleteRequest: boolean }, thunkAPI) => {
  try {
    const response: ApiResponse<undefined> = await api.post(`${apiEndpoints.userRequest.passPendingRequest}/${id}`, { data: { unlinkReason } });
    return { id, isUrgent: false, isDeleteRequest };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const rejectPendingRequest = createAsyncThunk("columns/rejectPendingRequest", async ({ id, rejectReason }: { id: string; rejectReason: string }, thunkAPI) => {
  try {
    const response: ApiResponse<undefined> = await api.put(`${apiEndpoints.userRequest.rejectPendingRequest}/${id}`, { data: { rejectReason } });
    return { id, isUrgent: false };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getReportVersionAsyncThunk = createAsyncThunk<Report, { trackingId: string; version: number; withDeleted: boolean }, { rejectValue: string }>(
  "lostItem/getReportVersionAsyncThunk",
  async ({ trackingId, version, withDeleted }, thunkAPI) => {
    try {
      const response: ApiResponse<Report> = await api.get(`${apiEndpoints.reportDetail.getLostItemInfoByVersion}?id=${trackingId}&version=${version}&withDeleted=${withDeleted}`);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch version information");
      }
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const updateLostItemByAdmin = createAsyncThunk<Report, { id: string | number; formData: FormData }, { rejectValue: string }>("lostItem/updateLostItemByAdmin", async ({ id, formData }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await apiWithFormDataMethods.put(`${apiEndpoints.userRequest.updateLostItemByAdmin}/${id}`, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const createLostItemByAdmin = createAsyncThunk<Report, { formData: FormData }, { rejectValue: string }>("lostItem/createLostItemByAdmin", async ({ formData }, thunkAPI) => {
  try {
    const response: ApiResponse<Report> = await apiWithFormDataMethods.post(`${apiEndpoints.userRequest.sendLostReport}`, formData);
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to create lost item request");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const deleteLostItem = createAsyncThunk<any, { discardReason: string; lostId: string }, { rejectValue: string }>("lostItem/deleteLostItem", async ({ discardReason, lostId }, thunkAPI) => {
  try {
    const response: ApiResponse<any> = await api.put(`${apiEndpoints.lost.deleteLostItem}/${lostId}`, { data: { discardReason } });

    if (response.success && response.statusCode === STATUS_CODE.SUCCESS) {
      return response;
    } else {
      throw new Error(response.message || "Failed to delete lost item.");
    }
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.message || "An error occurred");
  }
});

export const getPreMatchedFoundItemsAsyncThunk = createAsyncThunk<PrematchedFoundItems[], GetPrematchedFoundItemsProps, { rejectValue: string }>(
  "lostItem/getPreMatchedFoundItemsAsyncThunk",
  async ({ trackingId, targetDate }: GetPrematchedFoundItemsProps, thunkAPI) => {
    try {
      const response: ApiResponse<PrematchedFoundItems[]> = await api.post(`${apiEndpoints.found.getPrematchedFoundItems}/${trackingId}`);
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to fetch lost item information");
      }
    } catch (error: any) {
      console.error("error: ", error);
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const linkFoundItemWithLostItemAsyncThunk = createAsyncThunk<[], LinkFoundItemWithLostItemProps, { rejectValue: string }>(
  "lostItem/linkFoundItemWithLostItemAsyncThunk",
  async ({ lostId, foundId }: LinkFoundItemWithLostItemProps, thunkAPI) => {
    try {
      const response: ApiResponse<[]> = await api.post(`${apiEndpoints.lost.linkFoundItemWithLostItem}`, { data: { lostId, foundId } });
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to link found item with lost item");
      }
    } catch (error: any) {
      console.error("error: ", error);
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const createPrematchData = createAsyncThunk("report/createPrematchData", async (data: any, { rejectWithValue }) => {
  try {
    const response: ApiResponse<any> = await api.post(`${apiEndpoints.shippingOption.createPrematchData}`, { data });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Something went wrong! please try again later.");
  }
});

export const getAllShippingOptions = createAsyncThunk("report/getAllShippingOptions", async ({ lostId }: { lostId: string }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<ShippingOption[]> = await api.get(`${apiEndpoints.shippingOption.getAll}`, { params: { lostId } });
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message || "Something went wrong! please try again later.");
  }
});

export interface UpdateShippingOptionProps {
  id: string;
  shippingCost: number;
  handlingFee: number;
  shippingMarkup: number;
  courier: string;
  courierShippingOption: string;
  shippingBox: string;
  shippingBoxHeight: number;
  shippingBoxHeightUnit: string;
  shippingBoxWidth: number;
  shippingBoxWidthUnit: string;
  shippingBoxLength: number;
  shippingBoxLengthUnit: string;
  shippingBoxWeight: number;
  shippingBoxWeightUnit: string;
  measurementType: number;
  shippingBoundary: number;
  payByDate: string;
  cutOffDate: string;
  shippingOption: string;
}

export const updateShippingOptions = createAsyncThunk(
  "report/updateShippingOptions",
  async (data: UpdateShippingOptionProps, { rejectWithValue }) => {
    try {
      const response: ApiResponse<ShippingOption> = await api.put(`${apiEndpoints.shippingOption.update}/${data.id}`, {
        data,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Something went wrong! please try again later.");
    }
  }
);

export const cloneShippingOptions = createAsyncThunk(
  "report/cloneShippingOptions",
  async (data: UpdateShippingOptionProps, { rejectWithValue }) => {
    try {
      const response: ApiResponse<ShippingOption> = await api.post(`${apiEndpoints.shippingOption.clone}`, {
        data,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Something went wrong! please try again later.");
    }
  }
);

export interface GetPackageParams {
  lostId: string;
  foundId: string;
}

export const getPackage = createAsyncThunk<Package, GetPackageParams, { rejectValue: string }>("report/getPackage", async ({ lostId, foundId }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<Package> = await api.get(`${apiEndpoints.shippingOption.getPackage}`, { params: { lostId, foundId } });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching package:", error);
    return rejectWithValue(error.message || "Something went wrong! Please try again later.");
  }
});

export interface UpdatePackageParams {
  id: string;
  data: {
    heightPrimaryValue: number;
    heightPrimaryUnit: string;
    heightSecondaryValue: number;
    heightSecondaryUnit: string;
    widthPrimaryValue: number;
    widthPrimaryUnit: string;
    widthSecondaryValue: number;
    widthSecondaryUnit: string;
    lengthPrimaryValue: number;
    lengthPrimaryUnit: string;
    lengthSecondaryValue: number;
    lengthSecondaryUnit: string;
    weightPrimaryValue: number;
    weightPrimaryUnit: string;
    weightSecondaryValue: number;
    weightSecondaryUnit: string;
    measurementType: number;
    description: string;
    lost: string;
    found: string;
  };
}

export const updatePackage = createAsyncThunk<Package, UpdatePackageParams, { rejectValue: string }>("report/updatePackage", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<Package> = await api.put(`${apiEndpoints.shippingOption.updatePackage}/${id}`, { data });
    return response.data;
  } catch (error: any) {
    console.error("Error updating package:", error);
    return rejectWithValue(error.message || "Something went wrong! Please try again later.");
  }
});

export const sendPaymentRequest = createAsyncThunk<Report, { lost: string; found: string, selected: string[] }, { rejectValue: string }>("report/sendPaymentRequest", async ({ lost, found, selected }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<Report> = await api.post(`${apiEndpoints.found.sendPaymentRequest}`, { data: { lost, found, selected } });
    return response.data;
  } catch (error: any) {
    console.error("Error sending payment request:", error);
    return rejectWithValue(error.message || "Something went wrong! Please try again later.");
  }
});

export const updateLostItemCaseManager = createAsyncThunk("report/updateLostItemCaseManager", async ({ id, caseManager }: { id: string; caseManager: string }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<AdminUser> = await api.put(`${apiEndpoints.lost.updateCaseManager}/${id}`, { data: { caseManager } });
    if (response?.success && response?.statusCode === STATUS_CODE.SUCCESS) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update case manager.");
    }
  } catch (error: any) {
    return rejectWithValue(error?.response?.data || error.message);
  }
});

export const uploadShippingLabel = createAsyncThunk<Document, { lostId: string; formdata: FormData; isDelete: boolean }, { rejectValue: string }>(
  "report/uploadShippingLabel",
  async ({ lostId, formdata, isDelete }, { rejectWithValue }) => {
    try {
      const response: ApiResponse<Document> = await apiWithFormDataMethods.put(`${apiEndpoints.lost.uploadShippingLabel}/${lostId}`, formdata);
      if (response?.success) {
        return { ...response.data, isDelete };
      } else {
        throw new Error(response.message || "Failed to upload shipping label.");
      }
    } catch (error: any) {
      console.error("Error uploading shipping label:", error);
      return rejectWithValue(error.message || "Something went wrong! Please try again later.");
    }
  }
);

export const uploadAdditionalDoc = createAsyncThunk<{ isDelete: boolean; documents: Document[] }, { lostId: string; formdata: FormData; isDelete: boolean }, { rejectValue: string }>(
  "report/uploadAdditionalDoc",
  async ({ lostId, formdata, isDelete }, { rejectWithValue }) => {
    try {
      const response: ApiResponse<Document[]> = await apiWithFormDataMethods.put(`${apiEndpoints.lost.uploadAdditionalDoc}/${lostId}`, formdata);
      if (response?.success) {
        return { documents: response.data, isDelete };
      } else {
        throw new Error(response.message || "Failed to upload additional document.");
      }
    } catch (error: any) {
      console.error("Error uploading additional document:", error);
      return rejectWithValue(error.message || "Something went wrong! Please try again later.");
    }
  }
);

export const uploadShippingProof = createAsyncThunk<{
  isDelete: boolean;
  documents: Document[];
  deliveryDate?: string;
}, {
  lostId: string;
  formdata: FormData;
  isDelete: boolean;
  deliveryDate?: string;
}, {
  rejectValue: string;
}>(
  "report/uploadShippingProof",
  async ({ lostId, formdata, isDelete, deliveryDate }, { rejectWithValue }) => {
    try {
      const response: ApiResponse<Document[]> = await apiWithFormDataMethods.put(
        `${apiEndpoints.lost.uploadShippingProof}/${lostId}`,
        formdata
      );

      if (response?.success) {
        return {
          documents: response.data,
          isDelete,
          deliveryDate,
        };
      } else {
        throw new Error(response.message || "Failed to upload shipping proof.");
      }
    } catch (error: any) {
      console.error("Error uploading shipping proof:", error);
      return rejectWithValue(error.message || "Something went wrong! Please try again later.");
    }
  }
);

export const updateTrackingNumber = createAsyncThunk<Report, { lostId?: string; trackingNumber: string }, { rejectValue: string }>(
  "report/updateTrackingNumber",
  async ({ lostId, trackingNumber }, { rejectWithValue }) => {
    try {
      const response: ApiResponse<Report> = await api.put(`${apiEndpoints.lost.updateTrackingNumber}/${lostId}`, { data: { trackingNumber } });
      if (response?.success) {
        return response.data;
      } else {
        throw new Error(response.message || "Failed to upload tracking number.");
      }
    } catch (error: any) {
      console.error("Error uploading tracking number:", error);
      return rejectWithValue(error.message || "Something went wrong! Please try again later.");
    }
  }
);

export const getEmailContext = createAsyncThunk("report/getEmailContext", async (data: { templateId: string; lostId: string }, { rejectWithValue }) => {
  try {
    const response: ApiResponse<any> = await api.post(`${apiEndpoints.emailTemplate.getEmailContext}`, { data });
    if (response?.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to fetch email context.");
    }
  } catch (error: any) {
    console.error("Error fetching email context:", error);
    return rejectWithValue(error.message || "Something went wrong! Please try again later.");
  }
});

export const updateShippingOptionAddress = createAsyncThunk("report/updateShippingOptionAddress", async (data: UpdateShippingOptionAddressRequestPayload, { rejectWithValue }) => {
  try {
    const response: ApiResponse<ShippingOption> = await api.put(`${apiEndpoints.shippingOption.updateShippingOptionAddress}/${data.id}`, { data });
    if (response?.success) {
      return response.data;
    } else {
      throw new Error(response.message || "Failed to update shipping option address.");
    }
  } catch (error: any) {
    return rejectWithValue(error.message || "Something went wrong! Please try again later.");
  }
});
